import { supabase } from '@/lib/supabase';

export interface EmailQueueItem {
  id: string;
  notification_id: string;
  user_email: string;
  notification_type: 'milestone' | 'exception' | 'status_change' | 'delay' | 'delivery';
  title: string;
  message: string;
  tracking_number?: string;
  current_status?: string;
  current_location?: string;
  attempts: number;
  created_at: string;
}

class EmailService {
  private processingInterval: NodeJS.Timeout | null = null;
  private isProcessing = false;

  /**
   * Start processing email queue at regular intervals
   */
  startEmailQueueProcessor(intervalMs: number = 30000): void {
    if (this.processingInterval) {
      console.log('Email queue processor already running');
      return;
    }

    console.log('Starting email queue processor...');
    this.processingInterval = setInterval(() => {
      this.processEmailQueue();
    }, intervalMs);

    // Process immediately on start
    this.processEmailQueue();
  }

  /**
   * Stop processing email queue
   */
  stopEmailQueueProcessor(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      console.log('Email queue processor stopped');
    }
  }

  /**
   * Process pending emails in the queue
   */
  async processEmailQueue(): Promise<void> {
    if (this.isProcessing) {
      console.log('Email queue processing already in progress, skipping...');
      return;
    }

    this.isProcessing = true;

    try {
      // Get pending emails from queue
      const { data: pendingEmails, error } = await supabase.rpc('get_pending_emails', {
        p_limit: 10,
      });

      if (error) {
        // Check if error is due to missing function (migration not deployed)
        if (error.code === 'PGRST202' || error.message?.includes('Could not find the function')) {
          // Silently skip - migration not deployed yet
          return;
        }
        console.error('Error fetching pending emails:', error);
        return;
      }

      if (!pendingEmails || pendingEmails.length === 0) {
        return;
      }

      console.log(`Processing ${pendingEmails.length} pending emails...`);

      // Process each email
      for (const email of pendingEmails as EmailQueueItem[]) {
        await this.sendEmail(email);
      }
    } catch (error: any) {
      // Silently handle network errors (Failed to fetch) - these occur when:
      // 1. Database migration not deployed
      // 2. Network connectivity issues
      // 3. CORS issues in browser environment
      // These are expected in development/staging and shouldn't break the UI
      if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        return;
      }
      console.error('Error processing email queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Send individual email via Edge Function
   */
  private async sendEmail(emailItem: EmailQueueItem): Promise<void> {
    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('NEXT_PUBLIC_SUPABASE_URL not configured');
      }

      // Call Edge Function to send email
      const edgeFunctionUrl = `${supabaseUrl.replace('/rest/v1', '')}/functions/v1/send-shipment-email`;

      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          notificationId: emailItem.notification_id,
          userEmail: emailItem.user_email,
          title: emailItem.title,
          message: emailItem.message,
          type: emailItem.notification_type,
          trackingNumber: emailItem.tracking_number,
          currentStatus: emailItem.current_status,
          currentLocation: emailItem.current_location,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Failed to send email');
      }

      // Mark email as sent
      const { error: markError } = await supabase.rpc('mark_email_sent', {
        p_email_queue_id: emailItem.id,
        p_sent_at: new Date().toISOString(),
      });

      if (markError) {
        console.error('Error marking email as sent:', markError);
      } else {
        console.log(`Email sent successfully for notification ${emailItem.notification_id}`);
      }
    } catch (error: any) {
      console.error(`Error sending email for notification ${emailItem.notification_id}:`, error);

      // Mark email as failed
      const { error: markError } = await supabase.rpc('mark_email_failed', {
        p_email_queue_id: emailItem.id,
        p_error_message: error.message,
      });

      if (markError) {
        console.error('Error marking email as failed:', markError);
      }
    }
  }

  /**
   * Manually trigger email queue processing
   */
  async triggerEmailProcessing(): Promise<void> {
    await this.processEmailQueue();
  }
}

export const emailService = new EmailService();